import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lock, X, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * ChangePasswordModal
 * Usage: <ChangePasswordModal open={bool} onClose={fn} />
 */
export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault();
    if (!current || !next) return toast.error("Compila tutti i campi");
    if (next !== confirm) return toast.error("Le password non coincidono");
    if (next.length < 6) return toast.error("La nuova password deve essere di almeno 6 caratteri");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata!");
      setCurrent(""); setNext(""); setConfirm("");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/30 shadow-2xl"
        data-testid="change-password-modal">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-amber-400"/>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-amber-400/80">Sicurezza</div>
              <div className="text-lg font-bold text-slate-100">Cambia Password</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400"
            data-testid="change-password-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">Password attuale</label>
            <Input type="password" value={current} onChange={e => setCurrent(e.target.value)}
              data-testid="password-current-input"
              className="bg-slate-950 border-slate-700 text-slate-100"/>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">Nuova password (min 6 caratteri)</label>
            <Input type="password" value={next} onChange={e => setNext(e.target.value)}
              data-testid="password-new-input"
              className="bg-slate-950 border-slate-700 text-slate-100"/>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1 block">Conferma nuova password</label>
            <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              data-testid="password-confirm-input"
              className="bg-slate-950 border-slate-700 text-slate-100"/>
          </div>
        </div>
        <div className="p-5 border-t border-slate-800 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Annulla</Button>
          <Button type="submit" disabled={busy}
            data-testid="password-submit-btn"
            className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
            {busy ? "…" : <><Check className="w-4 h-4 mr-1"/>Conferma</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
